import { useState, useEffect } from 'react';
import {
  Button,
  ButtonSet,
  Slider,
  SkeletonPlaceholder,
  Toggle,
  Tooltip,
  ContentSwitcher,
  Switch,
  InlineNotification,
  ModalWrapper,
  ToastNotification,
  RadioTile,
  TileGroup,
} from 'carbon-components-react';

import { FormattedMessage, useIntl } from 'react-intl';
import messages from '../locales/messages';
import Cookies from 'js-cookie';
import Helmet from 'react-helmet';
import { FormModal } from 'src/common/utils/FormModal';
import { ModalMessage } from 'src/common/utils/modalMessage';
import './QRadarSiemPriceEstimator.scss';
import 'src/common/style/importantStyle.scss';
import 'src/common/style/common.scss';
import countryList from '../services/currencies.json';
import { segmentTracking } from '../lib/tracker';
import Footnote from './Footnote/Footnote';
import Thanks from './Thanks/Thanks';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import { usePrice } from '../hooks/usePrice';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import {
  ONPREM_DISCOUNT,
  UT30QradarSiem,
  maxEmployees,
} from '../constants/spread';
import { maxServers } from '../constants/spread';
import { swapCountryAndLanguage } from '../utils/swapCountryAndLanguage';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import * as URX_FORM from '../constants/urx-form';
import {
  LangDefaults,
  determineCountry,
  useLang,
} from 'src/common/hooks/widgets/useLang';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import {
  urxScript,
  urxScriptLoader,
} from 'src/common/hooks/widgets/globalDefinitions';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import { Price } from '../constants/prices';
import DisclaimerText from 'src/common/components/DisclaimerText/DisclaimerText';

export interface QRadarSiemPriceEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const QRadarSiemPriceEstimator = ({
  langCode,
  test,
  countryToTest,
}: QRadarSiemPriceEstimatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');
  // lang and userlocale is based out of ddo
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );
  /* Have urx form deault in us-en when on language us page */
  const [urxlang] = useState(localeCode.toString());
  const [localCurrency, setlocalCurrency] = useState<string>(localeCode);

  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);
  const [environment] = useEnv();
  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  const [modalLocalCurrency, setModalLocalCurrency] = useState('USD');
  const [modalLocalLanguage, setModalLocalLanguage] = useState('English');

  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);

  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);
  const intl = useIntl();

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
    test,
    localeCode,
  );

  const [employees, setEmployees] = useState(1000);
  const [servers, setServers] = useState(100);
  const [formComplete, setFormComplete] = useState(false);
  const [ndr, setNdr] = useState(true);
  const [onPrem, setOnPrem] = useState(true);
  const [CloudPrem, setCloudPrem] = useState(
    intl.formatMessage(messages.subheadline_sws),
  );
  const [DeliveryOption, setDeliveryOption] = useState('Software');
  const [InfoPrem, setInfoPrem] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerOpenMoreInfo, setDrawerOpenMoreInfo] = useState(false);
  const [tileValueSelected, setTileValueSelected] = useState('software');

  const isSmall = useSmallBreakpoint('560px');
  const { bpName, logo, companyId, desc, mssp } = useBPName(
    UT30QradarSiem,
    true,
  );
  const cookieURL = useCookiesURL();
  const [countDownPassed, setCountDownPassed] = useState(false);

  const [
    lowerPrice,
    higherPrice,
    fetchingError,
    lowerRaw,
    higherRaw,
    fpmPrices,
    epsPrices,
  ] = usePrice({
    langCode,
    currency,
    localeCode,
    country,
    employees,
    servers,
    ndr,
    onPrem,
  });

  const HandleErrorMessage = () => {
    if (Object.keys(fetchingError).length) {
      return (
        <ToastNotification
          title={<FormattedMessage {...messages.handling_title} />}
          subtitle={
            <>
              <FormattedMessage {...messages.handling_details1} />
              &nbsp;{lang}. <br />
              <FormattedMessage {...messages.handling_details2} />
              &nbsp;
              <a href="mailto:qrsizest@ibm.com">
                <FormattedMessage {...messages.handling_contact} />
              </a>
            </>
          }
        />
      );
    } else {
      return null;
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setCountDownPassed(!countDownPassed);
    }, 1000);
  }, []);

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
  };

  useEffect(() => {
    setServers(updateServers());
  }, [employees]);

  useEffect(() => {
    setCookie();
  }, [employees, servers, ndr, onPrem, higherPrice]);

  const setCookie = () => {
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            ' Employees:' +
            employees +
            ' | Servers:' +
            servers +
            ' | NDR:' +
            ndr +
            ' | Delivery Option: ' +
            DeliveryOption +
            ' | Estimated costs:' +
            lowerPrice +
            '-' +
            higherPrice +
            ` | FPM Prices for D0A7TZX with Qty 10, 100, 250 and discount ${
              ONPREM_DISCOUNT * 100
            }% :` +
            (fpmPrices as Price[]).map((fpm) => ' ' + fpm.price).toString() +
            ` | EPS Prices for D0A7SZX with Qty 10, 100, 250 and discount ${
              ONPREM_DISCOUNT * 100
            }% :` +
            (epsPrices as Price[]).map((eps) => ' ' + eps.price).toString() +
            (getBp() ? `|Preferred BP:${getBp()}` : ''),
          Q_ASSET_URL: window.location.href, // ISC - Asset URL initial
          Q_ASSET_NAME: 'IBM QRadar SIEM Estimator', // ISC - Asset Name
        },
      }),
    );

    console.log(Cookies.get('urxdcq'));
  };

  const updateServers = () => {
    return Math.round(employees / 10) || 1;
  };

  const getBp = () => {
    if (bpName) return bpName;
    if (logo) return companyId;
    if (desc) return desc;
    if (mssp) return mssp;

    const bpField = document.querySelector('#Q_BPNAME') as HTMLInputElement;
    return bpField ? bpField.value : '';
  };

  const replaceBpField = () => {
    const bpField = document.getElementById('Q_BPNAME') as HTMLInputElement;

    if (bpName && bpField) {
      //update label text
      const bpLabel = bpField.labels?.length
        ? bpField.labels[0]
        : new HTMLInputElement();
      bpLabel.innerHTML = intl.formatMessage(messages.preferred_bp);

      //replace the bp field by the bp name
      const bpText = document.createElement('p') as HTMLInputElement;
      bpText.innerText = bpName;
      bpText.style.fontSize = '1rem';
      bpText.style.fontWeight = 'bold';
      bpText.style.lineHeight = '2.5';
      bpField.parentNode?.replaceChild(bpText, bpField);
    }
  };

  /**
   *
   * @param value
   * @param setValue
   * @param max
   */
  const handleSliders = (
    value: number,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    max: number,
  ) => {
    if (value >= 1 && value <= max) {
      setValue(value);
    } else if (value >= max) {
      setValue(max);
    } else {
      setValue(1);
    }
  };

  return (
    <div className="qradar-siem widget-styles">
      <Helmet>
        <script src={urxScript}></script>
        <script src={urxScriptLoader}></script>
      </Helmet>
      {formComplete ? (
        <Thanks
          employees={employees}
          servers={servers}
          lowerPrice={`${lowerPrice}`}
          higherPrice={`${higherPrice}`}
          ndr={ndr}
          OnPrem={CloudPrem}
          bpName={bpName}
        />
      ) : (
        <div className="bx--grid" id="QRoCGrid">
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}
          <div className="bx--row mar3">
            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
              <div className="bx--row">
                <div
                  className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12"
                  id="countryModal"
                >
                  {localExist && toggleCountry ? (
                    <ModalWrapper
                      modalLabel={`Found country ${modalLocalCountry}. Access page in ${modalLocalLanguage} (${modalLocalCurrency}) `}
                      modalHeading={`The QRadar estimator is available in ${modalLocalLanguage} with local currency ${modalLocalCurrency}`}
                      size={'sm'}
                      primaryButtonText={'Cool! Take me there'}
                      secondaryButtonText={''}
                      buttonTriggerText={`Access page in ${modalLocalLanguage}`}
                      handleOpen={() => {
                        segmentTracking('CTA Clicked', {
                          CTA: 'Access Button for globalization',
                          location: 'Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                      handleSubmit={() => {
                        localStorage.setItem('langLocale', selectedLocale);
                        (window.location.href = window.location.href =
                          'https://www.ibm.com/' +
                          selectedLocale +
                          '/products/qradar-siem/pricing'),
                          segmentTracking('CTA Clicked', {
                            CTA: 'Submit button for globalization',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        return true;
                      }}
                    ></ModalWrapper>
                  ) : null}
                </div>
              </div>
              <div className="bx--row hide">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                      <TileGroup
                        className="qradar-siem__tilegroup"
                        name="radio tile group"
                        valueSelected={tileValueSelected}
                      >
                        {/* SasS should be hidden for chinese region */}
                        {country !== 'CHN' ? (
                          <RadioTile
                            className="qradar-siem__tile"
                            id="radio-tile-1-qradar-siem"
                            value="saas"
                            onClick={() => {
                              setCloudPrem('SIEM');
                              setDeliveryOption('');
                              setInfoPrem(true);
                              setOnPrem(false);
                              setTileValueSelected('');
                              segmentTracking('CTA Clicked', {
                                CTA: 'SIEM tile',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                          >
                            <div className="page_sub_heading mar9">
                              IBM QRadar{' '}
                              <FormattedMessage
                                {...messages.subheadline_saas}
                              />
                            </div>
                            <div className="page_body_2">
                              <span>
                                <ul>
                                  <li>
                                    <p className="page_body_2">
                                      <FormattedMessage
                                        {...messages.saas_bullet_one}
                                      />
                                    </p>
                                  </li>
                                  <li>
                                    <p className="page_body_2">
                                      <FormattedMessage
                                        {...messages.saas_bullet_two}
                                      />
                                    </p>
                                  </li>
                                  <li>
                                    <p className="page_body_2">
                                      <FormattedMessage
                                        {...messages.saas_bullet_three}
                                      />
                                    </p>
                                  </li>
                                  <li>
                                    <p className="page_body_2">
                                      <FormattedMessage
                                        {...messages.saas_bullet_four}
                                      />
                                    </p>
                                  </li>
                                  <li>
                                    <p className="page_body_2">
                                      <FormattedMessage
                                        {...messages.saas_bullet_five}
                                      />
                                    </p>
                                  </li>
                                </ul>
                              </span>
                            </div>
                          </RadioTile>
                        ) : null}
                        <RadioTile
                          id="radio-tile-2--qradar-siem"
                          value="software"
                          onClick={() => {
                            setCloudPrem(
                              intl.formatMessage(messages.subheadline_sws),
                            );
                            setDeliveryOption('Software');
                            setInfoPrem(false);
                            setOnPrem(true);
                            setTileValueSelected('software');
                            segmentTracking('CTA Clicked', {
                              CTA: 'SIEM (Software) tile',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        >
                          <div className="page_sub_heading mar9">
                            IBM QRadar{' '}
                            <FormattedMessage {...messages.subheadline_sws} />
                          </div>
                          <div className="page_body_2">
                            <div className="qradar_siem__tiletext">
                              <ul>
                                <li>
                                  <p className="page_body_2">
                                    <FormattedMessage
                                      {...messages.sws_bullet_one}
                                    />
                                  </p>
                                </li>
                                <li>
                                  <p className="page_body_2">
                                    <FormattedMessage
                                      {...messages.sws_bullet_three}
                                    />
                                  </p>
                                </li>
                                <li>
                                  <p className="page_body_2">
                                    <FormattedMessage
                                      {...messages.sws_bullet_four}
                                    />
                                  </p>
                                </li>
                                <li>
                                  <p className="page_body_2">
                                    <FormattedMessage
                                      {...messages.sws_bullet_five}
                                    />
                                  </p>
                                </li>
                                <li>
                                  <p className="page_body_2">
                                    <FormattedMessage
                                      {...messages.sws_bullet_six}
                                    />
                                  </p>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </RadioTile>
                      </TileGroup>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bx--row mar6">
                <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16 mar2">
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      {mssp ? (
                        <div>
                          <p>
                            <strong>
                              <FormattedMessage {...messages.contactBP} />
                            </strong>
                          </p>
                          <ToastNotification
                            aria-label="closes notification"
                            caption=""
                            hideCloseButton
                            kind="success"
                            lowContrast
                            role="log"
                            statusIconDescription=""
                            subtitle={
                              <FormattedMessage {...messages.bp_mssp} />
                            }
                            title={`${bpName} `}
                          />
                        </div>
                      ) : (
                        <p className="page_heading_3">
                          <FormattedMessage {...messages.scoping_questions} />
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="bx--row mar3 hide">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      {country === 'CHN' ? (
                        <ContentSwitcher
                          className="content-switcher"
                          selectedIndex={0}
                          size="xl"
                          onChange={(obj) => {
                            const { name, text } = obj;
                            setCloudPrem(name == undefined ? '' : name + '');
                            setDeliveryOption(
                              text == undefined ? '' : name + '',
                            );
                            setInfoPrem(false);
                            segmentTracking('CTA Clicked', {
                              CTA: `${name} Switcher`,
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                            setOnPrem(true);
                            setTileValueSelected('software');
                          }}
                        >
                          {/* SasS should be hidden for chinese region */}
                          <Switch
                            name={intl.formatMessage(messages.subheadline_sws)}
                            text="Software"
                          />
                        </ContentSwitcher>
                      ) : (
                        <ContentSwitcher
                          className="content-switcher"
                          selectedIndex={onPrem ? 1 : 0}
                          size="xl"
                          onChange={(obj) => {
                            const { name, text } = obj;
                            setCloudPrem(name == undefined ? '' : name + '');
                            setDeliveryOption(
                              text == undefined ? '' : name + '',
                            );
                            setInfoPrem(name === 'SIEM' ? true : false);
                            segmentTracking('CTA Clicked', {
                              CTA: `${name} Switcher`,
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                            setOnPrem(name === 'SIEM ' ? false : true);
                            setTileValueSelected(
                              name === 'SIEM' ? '' : 'software',
                            );
                          }}
                        >
                          {/* SasS should be hidden for chinese region */}
                          <Switch name="SIEM" text="" />
                          <Switch
                            name={intl.formatMessage(messages.subheadline_sws)}
                            text="Software"
                          />
                        </ContentSwitcher>
                      )}
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_sub_heading padding_24_3 mar1">
                        <FormattedMessage {...messages.welcome_msg_1} />
                        <span className="page_sub_heading qradar-siem__bluehighlight">
                          {` ${CloudPrem} `}
                        </span>
                        <FormattedMessage {...messages.price} />
                      </p>
                      <p className="page_body mar3">
                        <FormattedMessage {...messages.scoping_text} />
                      </p>
                    </div>
                  </div>
                  <div className="bx--row mar6">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Tooltip
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            CTA: 'workstation/employee tooltip',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'bottom' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body">
                            <FormattedMessage
                              {...messages.triggertext_workstations}
                            />
                          </span>
                        }
                      >
                        <span className="slide_text">
                          <FormattedMessage
                            {...messages.tooltip_workstations}
                          />
                          <a
                            onClick={() => {
                              segmentTracking('CTA Clicked', {
                                CTA: 'workstation/employee tooltip contact us',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                            target="_self"
                            data-ibm-contact="scheduler-link"
                            href="javascript:void(0)"
                            className="tooltip-anchor-color-light-blue"
                          >
                            <FormattedMessage {...messages.contact_us} />
                          </a>
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Slider
                        id="employees"
                        min={1}
                        max={maxEmployees}
                        value={employees}
                        onChange={({ value }) => {
                          segmentTracking('CTA Clicked', {
                            CTA: 'workstation/employee slider',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          handleSliders(value, setEmployees, maxEmployees);
                        }}
                      />
                    </div>
                  </div>
                  <div className="bx--row mar3">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Slider
                        id="servers"
                        min={1}
                        max={maxServers}
                        value={servers}
                        labelText={
                          <span className="page_body">
                            <FormattedMessage {...messages.labeltext_servers} />
                            <button
                              className="label-servers-link"
                              onClick={() => {
                                segmentTracking('CTA Clicked', {
                                  CTA: 'server more information button',
                                  location: 'Estimator',
                                  action: '',
                                  field: '',
                                });
                                setDrawerOpen(false);
                                setDrawerOpenMoreInfo(true);
                              }}
                            >
                              <FormattedMessage {...messages.more_info} />
                            </button>
                          </span>
                        }
                        onChange={({ value }) => {
                          segmentTracking('CTA Clicked', {
                            CTA: 'server slider',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          handleSliders(value, setServers, maxServers);
                        }}
                      />
                    </div>
                  </div>
                  <div className="bx--row mar3">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Tooltip
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            CTA: 'NDR tooltip',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'left' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body">
                            <FormattedMessage {...messages.tooltip_ndr} />
                          </span>
                        }
                      >
                        <span className="slide_text">
                          <FormattedMessage {...messages.tooltip_ndr_info} />
                          <a
                            onClick={() => {
                              segmentTracking('CTA Clicked', {
                                CTA: 'Ndr tooltip redirect',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                            }}
                            rel="noreferrer"
                            target="_blank"
                            href="https://www.ibm.com/qradar/security-qradar-ndr"
                            className="tooltip-anchor-color-light-blue"
                          >
                            IBM® QRadar® NDR
                          </a>
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Toggle
                        labelA={intl.formatMessage(messages.no)}
                        labelB={intl.formatMessage(messages.yes)}
                        defaultToggled
                        id="ndr-toggle-id"
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            CTA: 'NDR toggle',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          setNdr(!ndr);
                        }}
                      />
                    </div>
                  </div>
                  <div className="bx--row mar1 mar3">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      {/* Hiding till business comes back with the verbiage */}
                      {employees < 130 && servers < 13 && false ? (
                        <InlineNotification
                          kind="info"
                          role="na"
                          title="NDR Warning"
                          subtitle={'Loren Epsum'}
                          statusIconDescription="loren epsum"
                          hideCloseButton={true}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div id="footnote" className="div-only-lg">
                    <div className="bx--row">
                      <div className="bx--col-lg-16">
                        <Footnote numNote={1} />
                        <div className="footnote_text">
                          <DisclaimerText
                            localeCode={localeCode}
                            product="QRadar SIEM"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bx--col-lg-1"></div>
                <div className="bx--col-lg-5 bx--col-md-16 bx--col-sm-16">
                  <div className="bx--row">
                    <div className="qradar-siem__notif qradar-siem__notif_mar">
                      {!InfoPrem && (
                        <InlineNotification
                          kind="info"
                          role="onprem"
                          title={<FormattedMessage {...messages.inline_sws} />}
                          subtitle={
                            <FormattedMessage {...messages.subtitle_sws} />
                          }
                          statusIconDescription="On-Prem Info"
                          hideCloseButton={true}
                        />
                      )}
                      {InfoPrem && (
                        <InlineNotification
                          kind="info"
                          role="oncloud"
                          title={<FormattedMessage {...messages.inline_saas} />}
                          subtitle={
                            <FormattedMessage {...messages.saas_bullet_one} />
                          }
                          statusIconDescription="On-Cloud Info"
                          hideCloseButton={true}
                        />
                      )}
                    </div>
                  </div>
                  <div className="bx--row mar4 stickyNav">
                    <div className="sticky-nav qradar-siem__stickynav_mar price_table_comp">
                      <HandleErrorMessage />
                      <table className="cds--data-table cds--data-table--sm">
                        <thead>
                          <tr>
                            <th scope="col">
                              <div className="price_table_comp__heading">
                                <FormattedMessage {...messages.role} />
                              </div>
                            </th>
                            <th scope="col">
                              <div className="cds--table-header-label">
                                <div className=" price_table_comp__heading">
                                  <FormattedMessage {...messages.licences} />
                                </div>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody aria-live="polite">
                          <tr>
                            <td>
                              {' '}
                              <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                <FormattedMessage
                                  {...messages.triggertext_workstations}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="price_table_comp__quantity padding_8">{`${employees}`}</div>
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                <FormattedMessage
                                  {...messages.labeltext_servers}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="price_table_comp__quantity padding_8">{`${servers}`}</div>
                            </td>
                          </tr>

                          <tr>
                            <td>
                              <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                {ndr ? (
                                  <FormattedMessage {...messages.ndr_yes} />
                                ) : (
                                  <FormattedMessage {...messages.ndr_no} />
                                )}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <div id="price_toggle_one">
                        <p
                          id="widget.estimated-cost"
                          className="price_table_comp__bodycopy padding_24_2"
                        >
                          <FormattedMessage {...messages.estimated_costs} />
                          <sup className="qradar-siem__sup">1</sup>
                        </p>
                        <p className="padding_8 price_table_comp__price">
                          {lowerRaw == '0' && higherRaw == '0' ? (
                            <SkeletonPlaceholder className="qradar-siem__sticky-nav-skeleton" />
                          ) : (
                            `${lowerPrice} - ${higherPrice} *`
                          )}
                        </p>

                        <p className="price_table_comp__disclaimer price_table_comp_color padding_24_1">
                          * <FormattedMessage {...messages.asterisk_price} />{' '}
                        </p>
                      </div>
                      <ButtonSet className="qradar-siem__stack-button" stacked>
                        <Button
                          onClick={() => {
                            segmentTracking('CTA Clicked', {
                              CTA: 'Request a quote now',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                            initUrxForm(
                              environment,
                              URX_FORM.INSTANCE_ID,
                              URX_FORM.FORM_ID,
                              urxlang as IBMLocale,
                              companyId,
                            );
                            replaceBpField();
                            setDrawerOpen(true);
                            setDrawerOpenMoreInfo(false);
                            setCookie();
                          }}
                        >
                          <FormattedMessage {...messages.request_a_quote} />
                        </Button>
                      </ButtonSet>
                    </div>
                  </div>
                </div>
              </div>
              <div id="footnote" className="div-only-media">
                <div className="bx--row mar6">
                  <div className="bx--col-lg-9">
                    <div>
                      <Footnote numNote={1} />
                    </div>
                    <div className="footnote_text">
                      <DisclaimerText
                        localeCode={localeCode}
                        product="QRadar SIEM"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <FormModal
                pageTitle="QRadar Siem PE Page"
                object="QRadar Siem PE Page"
                productTitle={'QRadar SIEM price estimator'}
                pageUrl={''}
                location={'Estimator'}
                bpName={bpName}
                logo={logo}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                formId={URX_FORM.FORM_ID}
                lang={lang}
              ></FormModal>
              <ModalMessage
                setShowResponseModal={setDrawerOpenMoreInfo}
                showResponseModal={drawerOpenMoreInfo}
                textResponse={[
                  intl.formatMessage(messages['footnote-physical-server']),
                  intl.formatMessage(messages['footnote-virtual-server']),
                ]}
                messages={messages}
                modalTitle={intl.formatMessage(
                  messages['footnote-headline-two'],
                )}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRadarSiemPriceEstimator;
